import { useState, useEffect } from "react";
import { useFormik } from "formik";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { getSubCategory } from "../generatedRequest/subcategory";
import { getRegion } from "../app/request/regionRequest";
import FormSkeleton from "../components/Skeleton/FormSkeleton";
import { toast } from "react-toastify";
import { Check, X } from 'lucide-react';
import "./Compatible.css";

import { getMapping } from '../generatedRequest/compatible'

import CompatibleForm from "../generatedForms/CompatibleForm";
import CompatibleAttribute from "../generatedForms/CompatibleAttribute";
import CompatibilityDisplay from "../components/Compatibility/CompatibilityDisplay";

const CompatiblePage = () => {
  const [regions, setRegions] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingCompatibility, setCheckingCompatibility] = useState(false);
  const [isAccessoryCompatible, setIsAccessoryCompatible] = useState<boolean | null>(false); 
  const [showCompatibleSection, setShowCompatibleSection] = useState<boolean | null>(false); 
  
  // States for data from CompatibleForm
  const [selectedAccessory, setSelectedAccessory] = useState<any>(null);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [selectedCategories, setSelectedCategories] = useState<any[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [resetForm, setResetForm] = useState(false);

  // State for compatibility attributes from laptop subcategory
  const [compatibilityAttributes, setCompatibilityAttributes] = useState<any[]>([]);

  const [compatibilityData, setCompatibilityData] = useState<any>([]);
  const [compatibilityLogic, setCompatibilityLogic] = useState<any>([
   
    { label: 'Legacy Power Compatibility', value: false, key: 'legacyPowerCompatible'},
    { label: 'USB-C Power Compatibility', value: false, key: 'usbcPowerCompatible' },
    { label: 'USB-C Communication and Video Compatibility', value: false, key: 'usbcCommunicationAndVideoCompatible' },
    { label: 'USB-A Communication and Video Compatibility', value: false, key: 'usbaCommunicationAndVideoCompatible' }
  ]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch Regions
        const regionResponse = await getRegion(); 
        let regionsData = [];
        
        if (Array.isArray(regionResponse.data)) {
          regionsData = regionResponse.data;
        } else if (regionResponse.data && Array.isArray(regionResponse.data.data)) {
          regionsData = regionResponse.data.data;
        } else if (regionResponse.data && regionResponse.data.data && Array.isArray(regionResponse.data.data.data)) {
          regionsData = regionResponse.data.data.data;
        }
        setRegions(regionsData);

        // Fetch Categories
        const subcategoryResponse = await getSubCategory('limit=50');   
        let subcategoriesData = [];
        
        if (Array.isArray(subcategoryResponse.data)) {
          subcategoriesData = subcategoryResponse.data;
        } else if (subcategoryResponse.data && Array.isArray(subcategoryResponse.data.data)) {
          subcategoriesData = subcategoryResponse.data.data;
        } else if (subcategoryResponse.data && subcategoryResponse.data.data && Array.isArray(subcategoryResponse.data.data.data)) {
          subcategoriesData = subcategoryResponse.data.data.data;
        }
        setSubcategories(subcategoriesData);

        // Extract compatibility attributes from laptop subcategory
        const laptopSubcategory = subcategoriesData.find(
          (subcat: any) => subcat.subcategoryName === "laptop default"
        );
        
        if (laptopSubcategory && laptopSubcategory.compatibility) {
          setCompatibilityAttributes(laptopSubcategory.compatibility);
        }

      } catch (error) {
        toast.error("Failed to load data");
        setRegions([]);
        setSubcategories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handler to receive data from CompatibleForm
 const handleDataChange = (data: {
    selectedAccessory: any;
    selectedProduct: any;
    selectedCategories: any[];
    selectedRegion?: any;
  }) => {
    setSelectedAccessory(data.selectedAccessory);
    setSelectedProduct(data.selectedProduct);
    setSelectedCategories(data.selectedCategories);
    setSelectedRegion(data.selectedRegion || null); 
  };

  // Reset compatibility handler
  const handleResetCompatibility = () => {
    /* setSelectedAccessory(null);
    setSelectedProduct(null);
    setSelectedRegion(null);
    setCompatibilityData([]);
    setIsAccessoryCompatible(false);
      */ 
    setResetForm(prev => !prev);
    setTimeout(() => setResetForm(false), 0);
    setShowCompatibleSection(false);
    
  };

  // Check compatibility handler
  const handleCheckCompatibility = async () => {
    // Validation
    if (!selectedRegion) {
      toast.error("Please select a region");
      return;
    }
    if (!selectedProduct) {
      toast.error("Please select a product");
      return;
    }
    if (!selectedAccessory) {
      toast.error("Please select an accessory");
      return;
    }

    try {
      setCheckingCompatibility(true);
      setIsLoading(true);

      const payload = {
        regionId: selectedRegion._id || selectedRegion.id,
        productId: selectedProduct._id || selectedProduct.id,
        modelNumber: selectedAccessory.modelNumber
      };
      
      const response:any = await getMapping(payload);
      const apiResponse = response?.data.data || response;
      toast.success("Compatibility check completed!");
      console.log("API Response:", compatibilityLogic);

      if(apiResponse.accessory.subcategory == 'docking station' || apiResponse.accessory.subcategory == 'usb-hub'){
        const updatedCompatibilityLogic = compatibilityLogic.map((item:any) => ({
          ...item,
          value: apiResponse?.details[item.key] !== undefined ? apiResponse?.details[item.key] : item.value
        }));
        console.log("updatedCompatibilityLogic", updatedCompatibilityLogic);  
        setCompatibilityLogic(updatedCompatibilityLogic);
      }else{
        setCompatibilityLogic([{ label: apiResponse.accessory.subcategory+' Compatibility', value: true, key: apiResponse.accessory.subcategory+'_logic'},]);
      }
      
      setCompatibilityData(apiResponse);
      setIsAccessoryCompatible(apiResponse?.isCompatible || false);
      setShowCompatibleSection(true); // Show the compatibility section after checking
      setIsLoading(false);
      
    } catch (error: any) {
      toast.error(error?.message || "Failed to check compatibility");
    } finally {
      setCheckingCompatibility(false);
    }
  };

  if (loading) {
    return <FormSkeleton numberOfInput={5} />;
  }

  return (
   <>
    {isLoading && (
        <div className="overlay-loader">
            <div className="loader-container">
                <div className="spinner"></div>
                <div className="loader-text">Checking compatibility...</div>
            </div>
        </div>
    )}
    <div className="card p-5 max-width-full">
      <div className="card-header p-0 border-1 cursor-pointer">
        <div className="card-title m-0">
          <div
            role={"button"}
            className="btn btn-sm btn-light me-5"
          >
            <i className="fa-solid fa-arrow-left fs-2"></i>
          </div>
          <h3 className="fw-bolder m-0">Check Compatible</h3>
        </div>
      </div>

      <CompatibleForm 
        regions={regions} 
        categories={subcategories}
        onDataChange={handleDataChange}
        resetForm={resetForm}
      />

      {/* begin::Action */}
      <div className="d-flex flex-column flex-md-row mt-5 mb-3 w-100 w-md-100 justify-content-end">
        <button
          className="btn btn-secondary me-3 mb-3 mb-md-0"
          onClick={handleResetCompatibility}
          disabled={checkingCompatibility}
        >
          {checkingCompatibility ? "Checking..." : "Reset"}
        </button>
        <button
          className="btn btn-primary"
          onClick={handleCheckCompatibility}
          disabled={checkingCompatibility}
        >
          {checkingCompatibility ? "Checking..." : "Check Compatible"}
        </button>
      </div>
      {/* end::Action */}
    </div>

     <div className="card" style={{marginTop: "50px"}}>
        <CompatibleAttribute
          selectedAccessory={selectedAccessory}
          selectedProduct={selectedProduct}
          selectedCategories={selectedCategories}
          compatibilityAttributes={compatibilityAttributes}
        />
    </div>

    { showCompatibleSection &&
    (<div className="compatibility-card">
      {/* Table */}
      {/*<div className="table-container">
        <table className="compat-table">
          <tbody>
            <tr>
              <td><b>Accessory Compatible</b></td>
              <td>{(isAccessoryCompatible)?
                  <i className="fas fa-circle-check text-success fs-2x mb-3"></i> :
                  <i className="fas fa-circle-xmark text-danger fs-2x mb-3"></i>
                  }</td>
            </tr>
            {compatibilityData.map((item:any, index:any) => (
              <tr key={index}>
                <td>{item.label}</td>
                <td>
                  {(item.value)?
                  <i className="fas fa-circle-check text-success fs-2x mb-3"></i> :
                  <i className="fas fa-circle-xmark text-danger fs-2x mb-3"></i>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div> */}
      <div className="card max-width-full">
        <div className="card-header">
          <div className="card-title m-0">
            <h3 className="fw-bolder m-0">Accessory Compatibility</h3>
          </div>
        </div>
        <div className="card-body">
          {compatibilityData && (
            <CompatibilityDisplay 
              compatibilityData={compatibilityData.details.compatibilityNotesArray}
              accessory={compatibilityData}
              compatibilityLogic={compatibilityLogic}
            />
          )}
        </div>
      </div>
    </div>)}

   
   </>
  );
};

export default CompatiblePage;